import Swal from 'sweetalert2';
import { useCustomToast } from '@/shared/lib';
import { MAX_FILE_SIZE_MB, MAX_FILE_SIZE_BYTES, FILE_SIZE_EXCEEDS_LIMIT } from '@/shared/config';

type ValidationErrorCode = 'invalid_extension' | 'invalid_name' | 'invalid_size';
export type InvalidFile = { file: File; code: ValidationErrorCode; details?: string };
export const ALLOWED_DOCUMENT_EXTENSIONS = ['pdf', 'docx', 'txt', 'pptx', 'xlsx', 'xlsm'] as const;
export const ALLOWED_DOCUMENT_EXTENSIONS_LABEL = ALLOWED_DOCUMENT_EXTENSIONS.map(
  (extension) => `.${extension}`
).join(' | ');

export default function useFileValidation() {
  const allowedExtensions: string[] = [...ALLOWED_DOCUMENT_EXTENSIONS];
  const toast = useCustomToast();
  const documentPatternErrorMessage =
    'O nome do arquivo deve conter o padrão: [Prefixo]+[Numeração com 2 ou mais dígitos]+.[Sequencial].extensão. Exemplo: DA100.5.pdf';

  const extensionStripPattern = (() => {
    const sorted = [...allowedExtensions].sort((a, b) => b.length - a.length);
    return new RegExp(`\\.(${sorted.join('|')})$`, 'i');
  })();

  const getNameWithoutExtension = (fileName: string): string =>
    fileName.replace(extensionStripPattern, '');

  const getExtension = (name: string): string =>
    name.includes('.') ? name.split('.').pop()!.toLowerCase() : '';

  const isAllowedExtension = (file: File): boolean => {
    const ext = getExtension(file.name);
    return allowedExtensions.includes(ext);
  };

  const filterByAllowedExtensions = (files: FileList | null): File[] => {
    if (!files || files.length === 0) return [];
    return Array.from(files).filter(isAllowedExtension);
  };

  const validateFileSize = (file: File): { isValid: boolean; error?: string } => {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return {
        isValid: false,
        error: FILE_SIZE_EXCEEDS_LIMIT(MAX_FILE_SIZE_MB),
      };
    }
    return { isValid: true };
  };

  const basePrefixPattern = /^(DA|MQ|PR|IT)(\d{2,})\.(\d+)$/;

  const getTextToMatchForPrefix = (fileName: string): string => {
    const nameWithoutExtension = getNameWithoutExtension(fileName);
    const dashIndex = nameWithoutExtension.indexOf('-');
    return dashIndex !== -1
      ? nameWithoutExtension.substring(0, dashIndex).trim()
      : nameWithoutExtension;
  };

  const extractPrefixFromFileName = (fileName: string): string | null => {
    const textToMatch = getTextToMatchForPrefix(fileName);
    const match = basePrefixPattern.exec(textToMatch);
    if (!match) return null;
    const [, prefix, numbering, sequential] = match;
    return `${prefix}${numbering}.${sequential}`;
  };

  const getDuplicatePrefixesInBatch = (files: File[]): string[] => {
    const countByPrefix = new Map<string, number>();
    for (const file of files) {
      const prefix = extractPrefixFromFileName(file.name);
      if (prefix) {
        countByPrefix.set(prefix, (countByPrefix.get(prefix) ?? 0) + 1);
      }
    }
    return [...countByPrefix.entries()].filter(([, count]) => count > 1).map(([p]) => p);
  };

  const validateDocumentName = (
    fileName: string,
    isExternal?: boolean
  ): { isValid: boolean; error?: string } => {
    if (fileName.length > 500) {
      return {
        isValid: false,
        error: 'Nome do arquivo muito longo. Máximo 500 caracteres permitidos.',
      };
    }

    if (isExternal) {
      return { isValid: true };
    }

    const textToMatch = getTextToMatchForPrefix(fileName);
    const match = basePrefixPattern.exec(textToMatch);

    if (!match) {
      return {
        isValid: false,
        error: documentPatternErrorMessage,
      };
    }

    return { isValid: true };
  };

  const filterValidDocumentFiles = (
    files: FileList | null,
    isExternal?: boolean
  ): { valid: File[]; invalid: InvalidFile[] } => {
    if (!files || files.length === 0) return { valid: [], invalid: [] };

    const valid: File[] = [];
    const invalid: InvalidFile[] = [];

    Array.from(files).forEach((file) => {
      const sizeValidation = validateFileSize(file);
      if (!sizeValidation.isValid) {
        invalid.push({ file, code: 'invalid_size', details: sizeValidation.error });
        return;
      }

      if (!isAllowedExtension(file)) {
        invalid.push({ file, code: 'invalid_extension' });
        return;
      }

      const nameValidation = validateDocumentName(file.name, isExternal);
      if (!nameValidation?.isValid) {
        invalid.push({ file, code: 'invalid_name' });
        return;
      }

      valid.push(file);
    });

    return { valid, invalid };
  };

  const safeToastError = (title: string, message: string, timeout = 6000) => {
    try {
      if (toast?.showErrorToast) {
        toast.showErrorToast({ title, message, type: 'error' }, { timeout });
      } else {
        Swal.fire({ title, text: message, icon: 'error', timer: timeout, showConfirmButton: true });
      }
    } catch {
      Swal.fire({ title, text: message, icon: 'error', timer: timeout, showConfirmButton: true });
    }
  };

  const showError = (message: string) => {
    safeToastError('Erro', message);
  };

  const showValidationErrors = (errors: InvalidFile[]) => {
    if (!errors || errors.length === 0) return;

    const hasSizeError = errors.some((e) => e.code === 'invalid_size');
    const hasExtError = errors.some((e) => e.code === 'invalid_extension');
    const hasNameError = errors.some((e) => e.code === 'invalid_name');

    if (hasSizeError) {
      const sizeError = errors.find((e) => e.code === 'invalid_size');
      const errorMessage = sizeError?.details || FILE_SIZE_EXCEEDS_LIMIT(MAX_FILE_SIZE_MB);
      safeToastError('Erro', errorMessage);
      return;
    }

    if (hasExtError) {
      safeToastError(
        'Arquivo enviado com o formato incorreto.',
        `São aceitos os formatos: ${ALLOWED_DOCUMENT_EXTENSIONS_LABEL}`
      );
    }

    if (hasNameError) {
      safeToastError(
        'O formato do título está incorreto. Renomeie o arquivo e tente novamente.',
        documentPatternErrorMessage
      );
    }
  };

  const getAcceptAttribute = (): string => `.${allowedExtensions.join(',.')}`;

  return {
    getAcceptAttribute,
    isAllowedExtension,
    filterByAllowedExtensions,
    extractPrefixFromFileName,
    getDuplicatePrefixesInBatch,
    validateDocumentName,
    validateFileSize,
    filterValidDocumentFiles,
    showError,
    showValidationErrors,
    MAX_FILE_SIZE_MB,
    MAX_FILE_SIZE_BYTES,
  };
}
